import {createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, Link, redirect} from "@tanstack/react-router"
import { OperatorRuntimeSearch } from "@/lib/routes/operator-runtime-search"
import {
    ApprovalsHistorySearch, CapaListSearch, CreateCapaSearch, DispositionSearch,
    ProcessFlowSearch, SignupSearch, WorkOrderControlSearch, AnnotatorSearch, WorkOrderDetailSearch, CalibrationRecordNewSearch, CapacitySearch,
    DocumentsListSearch, QualityReportNewSearch,
} from "@/lib/routes/search-params"
import type { QueryClient } from "@tanstack/react-query"

import Layout from "@/components/layout";
import Home from "@/pages/Home";
import Login from '@/components/auth/Login'
import SignupPage from '@/pages/SignupPage'
import { PasswordResetConfirm } from "@/pages/PasswordResetConfirmForm";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";

// Default loading component for route transitions
function DefaultPendingComponent() {
    return (
        <div className="flex flex-col gap-4 p-6">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-full max-w-md" />
            <div className="flex gap-4 mt-4">
                <Skeleton className="h-32 w-full" />
            </div>
        </div>
    );
}

/** Pull the zod issues out of a router error, if that's what this is.
 *
 *  TanStack wraps a failed `validateSearch` in a SearchParamError whose
 *  `message` is the stringified zod issue array, so an invalid link rendered as
 *  a raw JSON blob in the user's face — `[{"validation":"uuid","code": ...}]`.
 *  Worth unwrapping rather than pretty-printing: a bad search param is a broken
 *  *link*, not a crash, and the two want different words and different exits. */
function searchParamIssues(error: unknown): { path: string[] }[] | null {
    const direct = (error as { issues?: unknown })?.issues;
    if (Array.isArray(direct)) return direct as { path: string[] }[];
    const message = (error as Error)?.message;
    if (typeof message !== "string" || !message.trim().startsWith("[")) return null;
    try {
        const parsed = JSON.parse(message);
        return Array.isArray(parsed) && parsed.every((i) => i && typeof i === "object" && "path" in i)
            ? parsed
            : null;
    } catch {
        return null;
    }
}

// Default error component for route errors
function DefaultErrorComponent({ error, reset }: { error: Error; reset?: () => void }) {
    const issues = searchParamIssues(error);
    if (issues) {
        const params = [...new Set(issues.map((i) => i.path?.join(".")).filter(Boolean))];
        return (
            <div className="flex items-center justify-center p-6">
                <Alert className="max-w-lg">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>This link isn't valid</AlertTitle>
                    <AlertDescription className="mt-2">
                        <p className="mb-4">
                            {params.length
                                ? `The link carries a malformed ${params.join(", ")} value.`
                                : "The link carries a malformed value."}{" "}
                            It may be a stale bookmark or a damaged copy-paste.
                        </p>
                        <Button variant="outline" size="sm" asChild>
                            <Link to="/">Go to home</Link>
                        </Button>
                    </AlertDescription>
                </Alert>
            </div>
        );
    }
    return <GenericErrorComponent error={error} reset={reset} />;
}

function GenericErrorComponent({ error, reset }: { error: Error; reset?: () => void }) {
    return (
        <div className="flex items-center justify-center p-6">
            <Alert variant="destructive" className="max-w-lg">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Something went wrong</AlertTitle>
                <AlertDescription className="mt-2">
                    <p className="mb-4">{error.message || "An unexpected error occurred"}</p>
                    {reset && (
                        <Button variant="outline" size="sm" onClick={reset}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Try again
                        </Button>
                    )}
                </AlertDescription>
            </Alert>
        </div>
    );
}

// Shown for any URL that matches no route. Without it TanStack renders its
// built-in fallback -- the bare string "Not Found" on an otherwise empty page,
// with no way back and nothing identifying the app. Easy to hit: a stale
// bookmark, a renamed route, or a hand-edited id.
function DefaultNotFoundComponent() {
    return (
        <div className="flex items-center justify-center p-6">
            <Alert className="max-w-lg">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Page not found</AlertTitle>
                <AlertDescription className="mt-2">
                    <p className="mb-4">
                        That URL doesn't match anything in UQMES. It may have moved, or the
                        link may be out of date.
                    </p>
                    <Button variant="outline" size="sm" asChild>
                        <Link to="/">Go to home</Link>
                    </Button>
                </AlertDescription>
            </Alert>
        </div>
    );
}

// Router context type for data prefetching
export interface RouterContext {
    queryClient: QueryClient
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
    component: () => <Layout/>
});


const homeRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/", component: () => <Home/>,
})

const loginRote = createRoute({
    getParentRoute: () => rootRoute, path: "/login", component: () => <Login/>,
})

const signupRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/signup",
    validateSearch: SignupSearch,
    component: () => <SignupPage/>,
})

const passwordResetRequestRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/password-reset-request",
    component: lazyRouteComponent(() => import("@/pages/PasswordResetConfirmForm"), "PasswordResetRequest"),
})

const passwordResetConfirmRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/reset-password/$uid/$token", component: () => {
        const {uid, token} = passwordResetConfirmRoute.useParams()
        return (<PasswordResetConfirm
            uid={uid}
            token={token}
            onSuccess={() => {
                // Navigate to login or show success message
                window.location.href = '/login'
            }}
        />)
    },
})


const trackerRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/tracker",
    component: lazyRouteComponent(() => import("@/pages/TrackerPage")),
})

const orderDetailsRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/orders/$orderNumber",
    component: lazyRouteComponent(() => import("@/pages/OrderDetailsPage"), "OrderDetailsPage"),
})

const partAnnotatorRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/partAnnotator/$modelId/$partId",
    component: lazyRouteComponent(() => import("@/pages/PartAnnotator"), "PartAnnotator"),
})

const heatMapViewerPartTypeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/heatMapViewer/partType/$partTypeId",
    component: lazyRouteComponent(() => import("@/pages/HeatMapViewer"), "HeatMapViewer"),
})

const heatMapViewerPartRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/heatMapViewer/part/$partId",
    component: lazyRouteComponent(() => import("@/pages/HeatMapViewer"), "HeatMapViewer"),
})

const QAPage = createRoute({
    getParentRoute: () => rootRoute, path: "/QA",
    component: lazyRouteComponent(() => import("@/pages/QaPartsPage")),
})

export const ordersCreateFormRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/editor/orders/new",
    component: lazyRouteComponent(() => import("./pages/OrderFormPage")),
})

export const ordersEditFormRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/editor/orders/$id/edit",
    component: lazyRouteComponent(() => import("./pages/OrderFormPage")),
})

const editLandingPageRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/Edit",
    component: lazyRouteComponent(() => import("./pages/EditLandingPage")),
})

const OrdersEditorPageRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/editor/orders",
    component: lazyRouteComponent(() => import("@/pages/editors/OrdersEditorPage")),
    loader: async ({ context }) => {
        const { prefetchOrdersEditor } = await import("@/pages/editors/OrdersEditorPage");
        prefetchOrdersEditor(context.queryClient);
    },
});

const PartsEditorRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "editor/parts",
    component: lazyRouteComponent(() => import("@/pages/editors/PartsEditorPage"), "PartsEditorPage"),
    loader: async ({ context }) => {
        const { prefetchPartsEditor } = await import("@/pages/editors/PartsEditorPage");
        prefetchPartsEditor(context.queryClient);
    },
})

export const partCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/parts/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditPartFormPage")),
});

export const partEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/parts/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditPartFormPage")),
});

export const partTypeCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/part-types/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditPartTypeFormPage")),
});

export const partTypeEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/part-types/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditPartTypeFormPage")),
});

const PartTypesEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: "editor/part-types",
    component: lazyRouteComponent(() => import("@/pages/editors/PartTypesEditorPage"), "PartTypesEditorPage"),
    loader: async ({ context }) => {
        const { prefetchPartTypesEditor } = await import("@/pages/editors/PartTypesEditorPage");
        prefetchPartTypesEditor(context.queryClient);
    },
})

export const processCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/processes/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditProcessFormPage")),
});

export const processEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/processes/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditProcessFormPage")),
});

const ProcessEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: "editor/processes",
    component: lazyRouteComponent(() => import("@/pages/editors/ProcessEditorPage"), "ProcessEditorPage"),
    loader: async ({ context }) => {
        const { prefetchProcessEditor } = await import("@/pages/editors/ProcessEditorPage");
        prefetchProcessEditor(context.queryClient);
    },
})

export const stepCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/steps/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditStepFormPage")),
});

export const stepEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/steps/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditStepFormPage")),
});

const StepEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: "editor/steps",
    component: lazyRouteComponent(() => import("@/pages/editors/StepEditorPage"), "StepsEditorPage"),
    loader: async ({ context }) => {
        const { prefetchStepsEditor } = await import("@/pages/editors/StepEditorPage");
        prefetchStepsEditor(context.queryClient);
    },
})

export const equipmentCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/equipment/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditEquipmentFormPage")),
});

export const equipmentEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/equipment/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditEquipmentFormPage")),
});

const EquipmentEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: "editor/equipment",
    component: lazyRouteComponent(() => import("@/pages/editors/EquipmentsEditorPage"), "EquipmentEditorPage"),
    loader: async ({ context }) => {
        const { prefetchEquipmentsEditor } = await import("@/pages/editors/EquipmentsEditorPage");
        prefetchEquipmentsEditor(context.queryClient);
    },
})

const ToolingEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: "editor/tooling",
    component: lazyRouteComponent(() => import("@/pages/editors/ToolingEditorPage"), "ToolingEditorPage"),
})

const toolingCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/editor/tooling/new",
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditFixtureFormPage")),
})

const toolingEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/editor/tooling/$id/edit",
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditFixtureFormPage")),
})

export const equipmentTypeCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/equipment-types/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditEquipmentTypeFormPage")),
});

export const equipmentTypeEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/equipment-types/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditEquipmentTypeFormPage")),
});

const EquipmentTypeEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: "editor/equipment-types",
    component: lazyRouteComponent(() => import("@/pages/editors/EquipmentTypeEditorPage"), "EquipmentTypeEditorPage"),
    loader: async ({ context }) => {
        const { prefetchEquipmentTypesEditor } = await import("@/pages/editors/EquipmentTypeEditorPage");
        prefetchEquipmentTypesEditor(context.queryClient);
    },
})

export const errorTypeCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/error-types/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditErrorTypeFormPage")),
});

export const errorTypeEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/error-types/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditErrorTypeFormPage")),
});

const ErrorTypeEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: "editor/error-types",
    component: lazyRouteComponent(() => import("@/pages/editors/ErrorTypeEditorPage"), "ErrorTypeEditorPage"),
    loader: async ({ context }) => {
        const { prefetchErrorTypesEditor } = await import("@/pages/editors/ErrorTypeEditorPage");
        prefetchErrorTypesEditor(context.queryClient);
    },
})

const SamplingRulesEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: "editor/sampling-rules",
    component: lazyRouteComponent(() => import("@/pages/editors/SamplingRulesEditorPage"), "SamplingRulesEditorPage"),
    loader: async ({ context }) => {
        const { prefetchSamplingRulesEditor } = await import("@/pages/editors/SamplingRulesEditorPage");
        prefetchSamplingRulesEditor(context.queryClient);
    },
})



const SamplingRuleSetsEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: "editor/sampling-rule-sets",
    component: lazyRouteComponent(() => import("@/pages/editors/SamplingRuleSetsEditorPage"), "SamplingRuleSetsEditorPage"),
    loader: async ({ context }) => {
        const { prefetchSamplingRuleSetsEditor } = await import("@/pages/editors/SamplingRuleSetsEditorPage");
        prefetchSamplingRuleSetsEditor(context.queryClient);
    },
})

export const samplingRuleSetsCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/sampling-rule-sets/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditSamplingRuleSetsFormPage")),
});

export const samplingRuleSetsEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/sampling-rule-sets/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditSamplingRuleSetsFormPage")),
});

const DocumentsRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/documents',
    component: lazyRouteComponent(() => import("@/pages/documents/DocumentsDashboardPage"), "DocumentsDashboardPage"),
})

const DocumentsListRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/documents/list',
    validateSearch: DocumentsListSearch,
    component: lazyRouteComponent(() => import("@/pages/editors/DocumentsEditorPage"), "DocumentsEditorPage"),
    loader: async ({ context }) => {
        const { prefetchDocumentsEditor } = await import("@/pages/editors/DocumentsEditorPage");
        prefetchDocumentsEditor(context.queryClient);
    },
})

export const DocumentDetailRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/documents/$id',
    component: lazyRouteComponent(() => import("@/pages/documents/DocumentDetailPage"), "DocumentDetailPage"),
})

export const DocumentCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/documents/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditDocumentFormPage")),
});

export const DocumentEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/documents/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditDocumentFormPage")),
});

export const ModelDetailRoute = createRoute({
    getParentRoute: () => rootRoute, path: 'details/$model/$id',
    component: lazyRouteComponent(() => import("@/pages/detail pages/ModeDetaisPageWrapper")),
})

export const WorkOrderEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: 'editor/work-orders',
    component: lazyRouteComponent(() => import("@/pages/editors/WorkOrdersEditorPage"), "WorkOrdersEditorPage"),
    loader: async ({ context }) => {
        const { prefetchWorkOrdersEditor } = await import("@/pages/editors/WorkOrdersEditorPage");
        prefetchWorkOrdersEditor(context.queryClient);
    },
})

export const workOrderCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/work-orders/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditWorkOrderFormPage")),
});

export const workOrderEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/work-orders/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditWorkOrderFormPage")),
});

export const companiesEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: 'editor/companies',
    component: lazyRouteComponent(() => import("@/pages/editors/CompaniesEditorPage"), "CompaniesEditorPage"),
    loader: async ({ context }) => {
        const { prefetchCompaniesEditor } = await import("@/pages/editors/CompaniesEditorPage");
        prefetchCompaniesEditor(context.queryClient);
    },
})

export const companiesCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/companies/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditCompanyFormPage")),
});

export const companiesEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/companies/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditCompanyFormPage")),
});

export const userEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: 'editor/users',
    component: lazyRouteComponent(() => import("@/pages/editors/UserEditorPage"), "UserEditorPage"),
    loader: async ({ context }) => {
        const { prefetchUsersEditor } = await import("@/pages/editors/UserEditorPage");
        prefetchUsersEditor(context.queryClient);
    },
})

export const usersCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/admin/users/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditUserFormPage")),
});

export const userManagementRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/admin/users',
    component: lazyRouteComponent(() => import("@/pages/admin/UserManagementPage"), "UserManagementPage"),
});

// Work Centers — the routing/surface primitive; see Documents/WORK_CENTER_DESIGN.md.
export const workCentersAdminRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/admin/work-centers',
    component: lazyRouteComponent(() => import("@/pages/admin/WorkCentersPage"), "default"),
});

export const bulkUserActionsRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/admin/users/bulk-invite',
    component: lazyRouteComponent(() => import("@/pages/admin/BulkUserActionsPage"), "BulkUserActionsPage"),
});

export const userDetailRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/admin/users/$id',
    component: lazyRouteComponent(() => import("@/pages/admin/UserDetailPage"), "UserDetailPage"),
});

export const usersEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/admin/users/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditUserFormPage")),
});

export const workOrderDetailRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/workorder/$workOrderId',
    validateSearch: WorkOrderDetailSearch,
    component: lazyRouteComponent(() => import("@/pages/WorkOrderDetailPage"), "WorkOrderDetailPage"),
});

export const workOrdersControlCenterRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/workorders',
    component: lazyRouteComponent(
        () => import("@/pages/workorders/WorkOrdersControlCenterPage"),
        "WorkOrdersControlCenterPage",
    ),
});

export const workOrderControlRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/workorder/$workOrderId/control',
    validateSearch: WorkOrderControlSearch,
    component: lazyRouteComponent(
        () => import("@/pages/workorders/WorkOrderControlPage"),
        "WorkOrderControlPage",
    ),
});

export const aiChatRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/ai-chat',
    component: lazyRouteComponent(() => import("@/pages/AiChatPage")),
});

export const threeDModelsEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: 'editor/3d-models',
    component: lazyRouteComponent(() => import("@/pages/editors/ThreeDModelsEditorPage"), "ThreeDModelsEditorPage"),
    loader: async ({ context }) => {
        const { prefetchThreeDModelsEditor } = await import("@/pages/editors/ThreeDModelsEditorPage");
        prefetchThreeDModelsEditor(context.queryClient);
    },
})

export const threeDModelsCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/3d-models/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditThreeDModelFormPage")),
});

export const threeDModelsEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/3d-models/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditThreeDModelFormPage")),
});

export const userProfileRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/profile',
    component: lazyRouteComponent(() => import("@/pages/UserProfilePage"), "UserProfilePage"),
});

export const settingsRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/settings',
    component: lazyRouteComponent(() => import("@/pages/settings/SettingsPage"), "SettingsPage"),
});

export const organizationSettingsRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/settings/organization',
    component: lazyRouteComponent(() => import("@/pages/settings/OrganizationSettingsPage"), "OrganizationSettingsPage"),
});

export const brandingSettingsRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/settings/branding',
    component: lazyRouteComponent(() => import("@/pages/settings/BrandingSettingsPage"), "BrandingSettingsPage"),
});

export const notificationRulesSettingsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/settings/notification-rules',
    component: lazyRouteComponent(
        () => import("@/pages/settings/NotificationRulesPage"),
        "NotificationRulesPage",
    ),
});

const NotificationRuleEditPage = lazyRouteComponent(
    () => import("@/pages/settings/NotificationRuleEditPage"),
    "NotificationRuleEditPage",
);

// Scope lives in the URL path — it's a structural identifier (which collection
// the row belongs to), not a filter. This keeps deep links self-describing
// and lets the editor fire exactly one retrieve query instead of fanning out.
function parseScopeParam(raw: string): 'tenant' | 'customer' | 'personal' {
    if (raw === 'tenant' || raw === 'customer' || raw === 'personal') return raw;
    throw new Error(`Invalid notification rule scope: ${raw}`);
}

export const notificationRuleNewRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/settings/notification-rules/$scope/new',
    component: function NotificationRuleNewRouteComponent() {
        const { scope } = notificationRuleNewRoute.useParams();
        return <NotificationRuleEditPage initialScope={parseScopeParam(scope)} />;
    },
});

export const notificationRuleEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/settings/notification-rules/$scope/$ruleId/edit',
    component: function NotificationRuleEditRouteComponent() {
        const { scope, ruleId } = notificationRuleEditRoute.useParams();
        return (
            <NotificationRuleEditPage
                ruleId={ruleId}
                initialScope={parseScopeParam(scope)}
            />
        );
    },
});

// Schedule edit pages — list lives inside the Notifications admin page
// as a top-level tab; only the edit routes are stand-alone.
const NotificationScheduleEditPage = lazyRouteComponent(
    () => import("@/pages/settings/NotificationScheduleEditPage"),
    "NotificationScheduleEditPage",
);

function parseAdminScopeParam(raw: string): 'tenant' | 'customer' {
    if (raw === 'tenant' || raw === 'customer') return raw;
    throw new Error(`Invalid notification schedule scope: ${raw}`);
}

export const notificationScheduleNewRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/settings/notification-schedules/$scope/new',
    component: function NotificationScheduleNewRouteComponent() {
        const { scope } = notificationScheduleNewRoute.useParams();
        return <NotificationScheduleEditPage initialScope={parseAdminScopeParam(scope)} />;
    },
});

export const notificationScheduleEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/settings/notification-schedules/$scope/$scheduleId/edit',
    component: function NotificationScheduleEditRouteComponent() {
        const { scope, scheduleId } = notificationScheduleEditRoute.useParams();
        return (
            <NotificationScheduleEditPage
                scheduleId={scheduleId}
                initialScope={parseAdminScopeParam(scope)}
            />
        );
    },
});

export const notificationDefaultsSettingsRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/settings/notifications',
    component: lazyRouteComponent(() => import("@/pages/settings/NotificationDefaultsPage"), "NotificationDefaultsPage"),
});

// External contacts — customer-side recipients of customer-scoped rules/schedules.
export const externalContactsRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/settings/notifications/external-contacts',
    component: lazyRouteComponent(() => import("@/pages/settings/ExternalContactsPage"), "ExternalContactsPage"),
});

export const externalContactNewRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/settings/notifications/external-contacts/new',
    component: lazyRouteComponent(() => import("@/pages/settings/ExternalContactFormPage"), "ExternalContactFormPage"),
});

export const externalContactEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/settings/notifications/external-contacts/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/settings/ExternalContactFormPage"), "ExternalContactFormPage"),
});

export const myNotificationsRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/profile/notifications',
    component: lazyRouteComponent(() => import("@/pages/MyNotificationsPage"), "MyNotificationsPage"),
});

// The in-app notification feed (awareness surface; commitments live at /inbox).
export const notificationFeedRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/notifications',
    component: lazyRouteComponent(() => import("@/pages/NotificationFeedPage"), "NotificationFeedPage"),
});

export const billingSettingsRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/settings/billing',
    component: lazyRouteComponent(() => import("@/pages/settings/BillingPage"), "BillingPage"),
});

export const milestonesEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/milestones',
    component: lazyRouteComponent(() => import("@/pages/editors/MilestonesEditorPage"), "MilestonesEditorPage"),
});

export const integrationsSettingsRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/settings/integrations',
    component: lazyRouteComponent(() => import("@/pages/settings/IntegrationsPage"), "IntegrationsPage"),
});

export const integrationDetailRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/settings/integrations/$id',
    component: lazyRouteComponent(() => import("@/pages/settings/IntegrationDetailPage"), "IntegrationDetailPage"),
});

export const qualityReportsEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: 'editor/quality-reports',
    component: lazyRouteComponent(() => import("@/pages/editors/QualityReportsEditorPage"), "QualityReportsEditorPage"),
    loader: async ({ context }) => {
        const { prefetchQualityReportsEditor } = await import("@/pages/editors/QualityReportsEditorPage");
        prefetchQualityReportsEditor(context.queryClient);
    },
});

export const qualityReportCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/quality-reports/new',
    validateSearch: QualityReportNewSearch,
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditQualityReportFormPage")),
});

export const qualityReportEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/quality-reports/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditQualityReportFormPage")),
});

export const annotatorPageRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/annotator',
    validateSearch: AnnotatorSearch,
    component: lazyRouteComponent(() => import("@/pages/AnnotatorPage"), "AnnotatorPage"),
});

export const analysisRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/analysis',
    component: lazyRouteComponent(() => import("@/pages/AnalysisPage")),
});

export const processFlowRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/process-flow',
    validateSearch: ProcessFlowSearch,
    component: lazyRouteComponent(() => import("@/pages/ProcessFlowPage")),
});

export const spcRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/spc',
    component: lazyRouteComponent(() => import("@/pages/SpcPage")),
});

// Quality Module Routes
export const qualityDashboardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality',
    component: lazyRouteComponent(() => import("@/pages/quality/QualityDashboardPage"), "QualityDashboardPage"),
    loader: async ({ context }) => {
        const { prefetchQualityDashboard } = await import("@/pages/quality/QualityDashboardPage");
        prefetchQualityDashboard(context.queryClient);
    },
});

export const changeControlRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/quality/change-control',
    component: lazyRouteComponent(() => import("@/pages/quality/ChangeControlPage"), "ChangeControlPage"),
});

export const pcrDetailRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/quality/change-control/pcrs/$id',
    component: lazyRouteComponent(() => import("@/pages/quality/PcrDetailPage"), "PcrDetailPage"),
});

export const pcoDetailRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/quality/change-control/pcos/$id',
    component: lazyRouteComponent(() => import("@/pages/quality/PcoDetailPage"), "PcoDetailPage"),
});

export const pcnDetailRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/quality/change-control/pcns/$id',
    component: lazyRouteComponent(() => import("@/pages/quality/PcnDetailPage"), "PcnDetailPage"),
});

export const capaListRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/quality/capas',
    validateSearch: CapaListSearch,
    component: lazyRouteComponent(() => import("@/pages/quality/CapaListPage"), "CapaListPage"),
});

export const capaCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/quality/capas/new',
    validateSearch: CreateCapaSearch,
    component: lazyRouteComponent(() => import("@/pages/quality/CreateCapaPage"), "CreateCapaPage"),
});

export const capaDetailRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/quality/capas/$id',
    component: lazyRouteComponent(() => import("@/pages/quality/CapaDetailPage"), "CapaDetailPage"),
});

export const ncrAnalysisRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/quality/ncrs',
    component: lazyRouteComponent(() => import("@/pages/quality/NcrAnalysisPage"), "NcrAnalysisPage"),
});

export const defectAnalysisRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/quality/defects',
    component: lazyRouteComponent(() => import("@/pages/quality/DefectAnalysisPage"), "DefectAnalysisPage"),
});

// Training Routes
export const trainingDashboardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/training',
    component: lazyRouteComponent(() => import("@/pages/quality/TrainingDashboardPage"), "TrainingDashboardPage"),
});

export const trainingOverridesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/training/overrides',
    component: lazyRouteComponent(() => import("@/pages/quality/TrainingOverridesPage"), "TrainingOverridesPage"),
})

export const trainingRecordsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/training/records',
    component: lazyRouteComponent(() => import("@/pages/quality/TrainingRecordsPage"), "TrainingRecordsPage"),
});

export const trainingTypesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/training/types',
    component: lazyRouteComponent(() => import("@/pages/quality/TrainingTypesPage"), "TrainingTypesPage"),
});

export const trainingMatrixRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/training/matrix',
    component: lazyRouteComponent(() => import("@/pages/quality/TrainingMatrixPage"), "TrainingMatrixPage"),
});

export const jobRolesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/training/roles',
    component: lazyRouteComponent(() => import("@/pages/quality/JobRolesPage"), "JobRolesPage"),
});

export const jobRoleNewRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/training/roles/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditJobRoleFormPage")),
});

export const jobRoleEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/training/roles/$roleId/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditJobRoleFormPage")),
});

export const trainingRecordFormRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/training/records/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditTrainingRecordFormPage")),
});
export const trainingRecordCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/training/records/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditTrainingRecordFormPage")),
});

export const trainingTypeFormRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/training/types/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditTrainingTypeFormPage")),
});
export const trainingTypeCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/training/types/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditTrainingTypeFormPage")),
});

// QA inspection task inbox — the QA landing surface (Home renders it directly
// for QA personas; this route lets leads/admins reach the same page).
export const qaInboxRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/inbox',
    component: lazyRouteComponent(() => import("@/pages/quality/QaHomePage"), "QaHomeRoute"),
});

// Operator landing surface (Home renders it directly for the Operator persona;
// this route lets leads/admins reach the same page).
export const operatorHomeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/operator',
    component: lazyRouteComponent(() => import("@/pages/operator/OperatorHomePage"), "OperatorHomeRoute"),
});

// Shift notes — lead authoring / management surface (operators read + ack on
// the operator home).
export const shiftNotesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/shift-notes',
    component: lazyRouteComponent(() => import("@/pages/production/ShiftNotesPage"), "default"),
});

// Calibration Routes
export const calibrationDashboardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/calibrations',
    component: lazyRouteComponent(() => import("@/pages/quality/CalibrationDashboardPage"), "CalibrationDashboardPage"),
});

// Every measurement definition in one list; editing stays in the step editor.
export const measurementDefinitionsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/measurement-definitions',
    component: lazyRouteComponent(() => import("@/pages/quality/MeasurementDefinitionsPage"), "MeasurementDefinitionsPage"),
});

export const calibrationRecordsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/calibrations/records',
    component: lazyRouteComponent(() => import("@/pages/quality/CalibrationRecordsPage"), "CalibrationRecordsPage"),
});

export const calibrationRecordFormRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/calibrations/records/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditCalibrationRecordFormPage")),
});
// What a gauge measured while it may have been out of tolerance (ISO 9001 7.1.5.2).
export const calibrationExposureRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/calibrations/records/$id/exposure',
    component: lazyRouteComponent(() => import("@/pages/quality/CalibrationExposurePage"), "CalibrationExposurePage"),
});
export const calibrationRecordCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/quality/calibrations/records/new',
    validateSearch: CalibrationRecordNewSearch,
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditCalibrationRecordFormPage")),
});

// Personal Routes
export const inboxRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/inbox',
    component: lazyRouteComponent(() => import("@/pages/InboxPage"), "InboxPage"),
});

// Production Routes
export const workOrdersRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/production/work-orders',
    component: lazyRouteComponent(() => import("@/pages/editors/QaWorkOrdersPage"), "QaWorkOrdersPage"),
});

export const dispositionsRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/production/dispositions',
    component: lazyRouteComponent(() => import("@/pages/editors/QaQuarantinePage"), "QaQuarantinePage"),
});

// Disposition Form Routes
export const dispositionCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/production/dispositions/new',
    validateSearch: DispositionSearch,
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditDispositionFormPage")),
});

export const dispositionEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/production/dispositions/$id/edit',
    validateSearch: DispositionSearch,
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditDispositionFormPage")),
});

// Admin Routes
export const auditLogRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/admin/audit-log',
    component: lazyRouteComponent(() => import("@/pages/editors/HistoryPage"), "AuditLogViewerPage"),
    loader: async ({ context }) => {
        const { prefetchAuditLogEditor } = await import("@/pages/editors/HistoryPage");
        prefetchAuditLogEditor(context.queryClient);
    },
});

export const masterWorkbookRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/admin/master-workbook',
    component: lazyRouteComponent(() => import("@/pages/admin/MasterWorkbookPage")),
});

// Approval Templates Routes
export const approvalTemplatesEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: 'editor/approval-templates',
    component: lazyRouteComponent(() => import("@/pages/editors/ApprovalTemplatesEditorPage"), "ApprovalTemplatesEditorPage"),
    loader: async ({ context }) => {
        const { prefetchApprovalTemplatesEditor } = await import("@/pages/editors/ApprovalTemplatesEditorPage");
        prefetchApprovalTemplatesEditor(context.queryClient);
    },
});

export const approvalTemplateCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/approval-templates/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditApprovalTemplateFormPage")),
});

export const approvalTemplateEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/approval-templates/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditApprovalTemplateFormPage")),
});

// Approvals Overview Routes
export const approvalsOverviewRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/approvals',
    component: lazyRouteComponent(() => import("@/pages/approvals/ApprovalsOverviewPage"), "ApprovalsOverviewPage"),
    loader: async ({ context }) => {
        const { prefetchApprovalsOverview } = await import("@/pages/approvals/ApprovalsOverviewPage");
        prefetchApprovalsOverview(context.queryClient);
    },
});

export const approvalsHistoryRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/approvals/history',
    validateSearch: ApprovalsHistorySearch,
    component: lazyRouteComponent(() => import("@/pages/approvals/ApprovalsHistoryPage"), "ApprovalsHistoryPage"),
    loader: async ({ context }) => {
        const { prefetchApprovalsHistory } = await import("@/pages/approvals/ApprovalsHistoryPage");
        prefetchApprovalsHistory(context.queryClient);
    },
});

// Document Types Routes
export const documentTypesEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: 'editor/document-types',
    component: lazyRouteComponent(() => import("@/pages/editors/DocumentTypesEditorPage"), "DocumentTypesEditorPage"),
    loader: async ({ context }) => {
        const { prefetchDocumentTypesEditor } = await import("@/pages/editors/DocumentTypesEditorPage");
        prefetchDocumentTypesEditor(context.queryClient);
    },
});

export const documentTypeCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/document-types/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditDocumentTypeFormPage")),
});

export const documentTypeEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/document-types/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditDocumentTypeFormPage")),
});

// Groups Routes
export const groupsEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: 'editor/groups',
    component: lazyRouteComponent(() => import("@/pages/editors/GroupsEditorPage"), "GroupsEditorPage"),
    loader: async ({ context }) => {
        const { prefetchGroupsEditor } = await import("@/pages/editors/GroupsEditorPage");
        prefetchGroupsEditor(context.queryClient);
    },
});

export const groupDetailRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/editor/groups/$id',
    component: lazyRouteComponent(() => import("@/pages/editors/GroupDetailPage"), "GroupDetailPage"),
});

// Heatmap Route
export const heatmapRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/heatmap',
    component: lazyRouteComponent(() => import("@/pages/HeatMapViewer"), "HeatMapViewer"),
});

// Error Pages
export const forbiddenRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/forbidden',
    component: lazyRouteComponent(() => import("@/pages/ForbiddenPage")),
});

// Dev Tools
export const schemaAuditRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/dev/schema-audit',
    component: lazyRouteComponent(() => import("@/pages/SchemaAuditPage"), "SchemaAuditPage"),
});

// Design prototype: the "perfect" operator home (mock data, no model constraints)
export const operatorHomePrototypeRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/dev/operator-home',
    component: lazyRouteComponent(() => import("@/pages/dev/OperatorHomePrototype"), "OperatorHomePrototype"),
});

// Design prototype: the list tier (work queue + checks due lenses, mock data)
export const workQueuePrototypeRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/dev/work-queue',
    component: lazyRouteComponent(() => import("@/pages/dev/WorkQueuePrototype"), "WorkQueuePrototype"),
});

// Design prototype: the "perfect" QA inspector home (mock data, no model constraints)
export const qaHomePrototypeRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/dev/qa-home',
    component: lazyRouteComponent(() => import("@/pages/dev/QaHomePrototype"), "QaHomePrototype"),
});

// Preview: per-role home landings (real block stacks, role switcher, no login needed)
export const homeLandingsSpikeRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/dev/home-landings',
    component: lazyRouteComponent(() => import("@/pages/dev/HomeLandingsSpike"), "HomeLandingsSpike"),
});

// Design prototype: operator training / competency matrix (mock data, no backend)
export const trainingMatrixPrototypeRoute = createRoute({
    getParentRoute: () => rootRoute, path: '/dev/training-matrix',
    component: lazyRouteComponent(() => import("@/pages/dev/TrainingMatrixPrototype"), "TrainingMatrixPrototype"),
});

// Reman Routes
export const coresEditorRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reman/cores',
    component: lazyRouteComponent(() => import("@/pages/editors/CoresEditorPage"), "CoresEditorPage"),
    loader: async ({ context }) => {
        const { prefetchCoresEditor } = await import("@/pages/editors/CoresEditorPage");
        prefetchCoresEditor(context.queryClient);
    },
});

export const coreDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reman/cores/$id',
    component: lazyRouteComponent(() => import("@/pages/reman/CoreDetailPage"), "CoreDetailPage"),
});

export const coreReceiveRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reman/cores/receive',
    component: lazyRouteComponent(() => import("@/pages/reman/CoreReceiveFormPage"), "CoreReceiveFormPage"),
});

// Cores received in bulk (counted, unidentified) and given identities one at a time.
export const coreLotsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reman/core-lots',
    component: lazyRouteComponent(() => import("@/pages/reman/CoreLotsPage"), "CoreLotsPage"),
});

export const coreReceiveBatchRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reman/cores/receive-batch',
    component: lazyRouteComponent(() => import("@/pages/reman/CoreReceiveBatchPage"), "CoreReceiveBatchPage"),
});

// Rebuild scope authoring. `/editor/` per the URL convention: this is engineering
// master data, not an operational surface.
export const repairCodesEditorRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/editor/repair-codes',
    component: lazyRouteComponent(() => import("@/pages/editors/RepairCodesEditorPage"), "RepairCodesEditorPage"),
    loader: async ({ context }) => {
        const { prefetchRepairCodesEditor } = await import("@/pages/editors/RepairCodesEditorPage");
        prefetchRepairCodesEditor(context.queryClient);
    },
});

export const repairCodeNewRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/editor/repair-codes/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/RepairCodeFormPage"), "RepairCodeFormPage"),
});

export const repairCodeEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/editor/repair-codes/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/RepairCodeFormPage"), "RepairCodeFormPage"),
});

export const rebuildLevelsEditorRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/editor/rebuild-levels',
    component: lazyRouteComponent(() => import("@/pages/editors/RebuildLevelsEditorPage"), "RebuildLevelsEditorPage"),
    loader: async ({ context }) => {
        const { prefetchRebuildLevelsEditor } = await import("@/pages/editors/RebuildLevelsEditorPage");
        prefetchRebuildLevelsEditor(context.queryClient);
    },
});

export const rebuildLevelNewRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/editor/rebuild-levels/new',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/RebuildLevelFormPage"), "RebuildLevelFormPage"),
});

export const rebuildLevelEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/editor/rebuild-levels/$id/edit',
    component: lazyRouteComponent(() => import("@/pages/editors/forms/RebuildLevelFormPage"), "RebuildLevelFormPage"),
});

export const rebuildQueueRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reman/rebuild-queue',
    component: lazyRouteComponent(() => import("@/pages/reman/RebuildQueuePage"), "RebuildQueuePage"),
});

export const coreRebuildRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reman/cores/$id/rebuild',
    component: lazyRouteComponent(() => import("@/pages/reman/RebuildPlanPage"), "RebuildPlanPage"),
});

// Teardown is run in the DWI operator runtime now — directions and capture for the step
// the unit is at — like any part's work (Documents/CORE_AS_PART_DESIGN.md). The old
// standalone screen skipped the process steps entirely. Kept as a redirect so bookmarks
// and old links land on the core's page, which opens the unit in the runtime.
export const coreDisassemblyRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reman/cores/$id/disassembly',
    beforeLoad: ({ params }) => {
        throw redirect({ to: '/reman/cores/$id', params: { id: params.id } });
    },
});

// Receiving (purchased material) Routes
export const materialLotsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/material-lots',
    // `tab` opens a lens directly — the home card's "All" opens Late.
    validateSearch: (s: Record<string, unknown>): { tab?: string } =>
        typeof s.tab === "string" ? { tab: s.tab } : {},
    component: lazyRouteComponent(() => import("@/pages/production/MaterialsPage"), "MaterialsPage"),
});
export const receiveLotsBatchRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/material-lots/receive',
    component: lazyRouteComponent(() => import("@/pages/production/ReceiveLotsBatchPage"), "ReceiveLotsBatchPage"),
});
export const receivingInspectionQueueRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/receiving-inspection',
    component: lazyRouteComponent(() => import("@/pages/production/ReceivingInspectionQueuePage"), "ReceivingInspectionQueuePage"),
});
export const receivingInspectionRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/receiving-inspection/$lotId',
    component: lazyRouteComponent(() => import("@/pages/production/ReceivingInspectionPage"), "ReceivingInspectionPage"),
});
export const incomingHubRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/incoming',
    component: lazyRouteComponent(() => import("@/pages/production/IncomingHubPage"), "IncomingHubPage"),
});
export const outsideProcessingBoardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/outside-processing',
    component: lazyRouteComponent(() => import("@/pages/production/OutsideProcessingBoardPage"), "OutsideProcessingBoardPage"),
});
export const receivingPlansRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/receiving-plans',
    component: lazyRouteComponent(() => import("@/pages/production/ReceivingInspectionPlansPage"), "ReceivingInspectionPlansPage"),
});
export const receivingPlanEditorRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/receiving-plans/$stepId',
    component: lazyRouteComponent(() => import("@/pages/production/ReceivingInspectionPlanEditorPage"), "ReceivingInspectionPlanEditorPage"),
});
// Standalone (process-free) DWI authoring for a receiving plan. Reuses the same
// SubstepEditorPage as the in-process flow editor — that page tolerates a
// missing processId (no "back to process flow" link, falls back to the plan).
export const receivingPlanSubstepsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/receiving-plans/$stepId/substeps',
    component: lazyRouteComponent(() => import("@/pages/editors/SubstepEditorPage"), "SubstepEditorPage"),
});
export const supplierQualityRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/supplier-quality',
    component: lazyRouteComponent(() => import("@/pages/production/SupplierQualityPage"), "SupplierQualityPage"),
});
export const supplierQualificationsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/supplier-qualifications',
    component: lazyRouteComponent(() => import("@/pages/production/SupplierQualificationsPage"), "SupplierQualificationsPage"),
});
export const supplierQualificationNewRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/supplier-qualifications/new',
    component: lazyRouteComponent(() => import("@/pages/production/SupplierQualificationFormPage"), "SupplierQualificationFormPage"),
});
export const supplierQualificationEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/supplier-qualifications/$qualId/edit',
    component: lazyRouteComponent(() => import("@/pages/production/SupplierQualificationFormPage"), "SupplierQualificationFormPage"),
});
export const partApprovalsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/part-approvals',
    component: lazyRouteComponent(() => import("@/pages/production/PartApprovalsPage"), "PartApprovalsPage"),
});
export const partApprovalNewRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/part-approvals/new',
    component: lazyRouteComponent(() => import("@/pages/production/PartApprovalFormPage"), "PartApprovalFormPage"),
});
export const partApprovalEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/production/part-approvals/$approvalId/edit',
    component: lazyRouteComponent(() => import("@/pages/production/PartApprovalFormPage"), "PartApprovalFormPage"),
});

export const harvestedComponentsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reman/components',
    component: lazyRouteComponent(() => import("@/pages/reman/HarvestedComponentsPage"), "HarvestedComponentsPage"),
});

export const remanDashboardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reman',
    component: lazyRouteComponent(() => import("@/pages/reman/RemanDashboardPage"), "RemanDashboardPage"),
});

const dwiSpikeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/dwi-spike',
    component: lazyRouteComponent(() => import("@/pages/DwiSpikePage"), "DwiSpikePage"),
});

const substepEditorRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/editor/processes/$processId/steps/$stepId/substeps',
    component: lazyRouteComponent(() => import("@/pages/editors/SubstepEditorPage"), "SubstepEditorPage"),
});


/** Shown when a deep link carries a malformed id — a stale bookmark, a
 *  truncated scan, a hand-edited URL. Better than a blank shell, and it
 *  names the station's way out. */
function OperatorRuntimeSearchError() {
    return (
        <div className="mx-auto max-w-lg p-8 text-center">
            <h1 className="text-lg font-semibold">This link isn't valid</h1>
            <p className="mt-2 text-sm text-muted-foreground">
                The work link you followed is missing or has a malformed id. It may be a
                stale bookmark or a damaged scan. Start the job again from your queue.
            </p>
            <Link to="/production/operator" className="mt-4 inline-block underline">
                Back to my queue
            </Link>
        </div>
    );
}

const operatorSubstepRuntimeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/operator/steps/$stepId/substeps',
    component: lazyRouteComponent(
        () => import("@/pages/operator/OperatorSubstepRuntimePage"),
        "OperatorSubstepRuntimePage",
    ),
    // Zod rather than hand-rolled narrowing, for three reasons:
    //
    //  1. `.optional()` gives genuinely optional keys. The previous validator
    //     returned an object literal with every key present and merely allowed
    //     to be undefined, so TypeScript demanded that callers spell out every
    //     key — which is why producers padded with `material_lot: undefined,
    //     osp_shipment: undefined, unit: undefined, debug: undefined` or gave
    //     up and cast `as never`.
    //  2. The id params are actually validated, not just narrowed to `string`.
    //     This route is a deep-link target: a wrong `execution` means the
    //     operator captures against the wrong step execution, which is a
    //     quality-record problem, so a malformed one must not reach the page.
    //  3. Numeric params are coerced once here instead of at each read site.
    //
    // Strict vs lenient is deliberate. The ids throw, because acting on the
    // wrong record is worse than showing an error. The cosmetic params
    // (`at`, `unit`) use `.catch()` so a damaged URL resumes at the start
    // rather than bricking a station mid-shift — the page already clamps both.
    //
    // No `.default()` anywhere on purpose: defaults are written back into the
    // URL by the router, so adding one would start appending `?at=0` to links
    // that don't have it today. The page's existing `search.at ?? 0` handles it.
    validateSearch: OperatorRuntimeSearch,
    // Without this an invalid id throws past the route and blanks the app
    // shell. VALIDATE_SEARCH failures land here instead.
    errorComponent: OperatorRuntimeSearchError,
});

const schedulingGanttRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/schedule",
    component: lazyRouteComponent(() => import("@/pages/SchedulingGanttPage"), "SchedulingGanttPage"),
})

const schedulingCalendarRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/calendar",
    component: lazyRouteComponent(() => import("@/pages/scheduling/SchedulingCalendarPage"), "SchedulingCalendarPage"),
})

const laborHoursRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/labor-hours",
    component: lazyRouteComponent(() => import("@/pages/scheduling/LaborHoursPage"), "LaborHoursPage"),
})

const stagingRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/staging",
    component: lazyRouteComponent(() => import("@/pages/scheduling/StagingPage"), "StagingPage"),
})

const capacityPlanningRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/capacity",
    validateSearch: CapacitySearch,
    component: lazyRouteComponent(() => import("@/pages/scheduling/CapacityPlanningPage"), "CapacityPlanningPage"),
})

const forecastRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/forecast",
    component: lazyRouteComponent(() => import("@/pages/scheduling/ForecastPage"), "ForecastPage"),
})

// Production › Orders — the staff order book and order page.
const orderBookRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/orders",
    component: lazyRouteComponent(() => import("@/pages/production/OrderBookPage"), "OrderBookPage"),
})
const staffOrderRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/orders/$orderId",
    component: lazyRouteComponent(() => import("@/pages/production/StaffOrderPage"), "StaffOrderPage"),
})

const requirementsRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/requirements",
    component: lazyRouteComponent(() => import("@/pages/scheduling/RequirementsPage"), "RequirementsPage"),
})

// Scheduling setup — the tables the solver sizes and routes work from. Under
// /production/ with the other scheduling surfaces.
const stepTimingsRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/step-timings",
    component: lazyRouteComponent(() => import("@/pages/scheduling/setup/StepTimingsPage"), "StepTimingsPage"),
})



const stepEquipmentAffinitiesRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/step-equipment-affinities",
    component: lazyRouteComponent(() => import("@/pages/scheduling/setup/StepEquipmentAffinitiesPage"), "StepEquipmentAffinitiesPage"),
})



const workCenterChangeoversRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/work-center-changeovers",
    component: lazyRouteComponent(() => import("@/pages/scheduling/setup/WorkCenterChangeoversPage"), "WorkCenterChangeoversPage"),
})



const MaterialsEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/editor/materials",
    component: lazyRouteComponent(() => import("@/pages/editors/MaterialsEditorPage"), "MaterialsEditorPage"),
})

const materialCreateRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/editor/materials/new",
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditMaterialFormPage")),
})

const materialEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/editor/materials/$id/edit",
    component: lazyRouteComponent(() => import("@/pages/editors/forms/EditMaterialFormPage")),
})

const receivingMetricsRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/receiving-metrics",
    component: lazyRouteComponent(() => import("@/pages/production/ReceivingMetricsPage"), "ReceivingMetricsPage"),
})

const cycleCountsRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/cycle-counts",
    component: lazyRouteComponent(() => import("@/pages/production/CycleCountsPage"), "CycleCountsPage"),
})

const cycleCountRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/cycle-counts/$countId",
    component: lazyRouteComponent(() => import("@/pages/production/CycleCountPage"), "CycleCountPage"),
})

const locationsRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/locations",
    component: lazyRouteComponent(() => import("@/pages/production/LocationsPage"), "LocationsPage"),
})

const locationDetailRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/locations/$locationId",
    component: lazyRouteComponent(() => import("@/pages/production/LocationDetailPage"), "LocationDetailPage"),
})

const shipmentsRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/shipments",
    component: lazyRouteComponent(() => import("@/pages/production/ShippingPage"), "ShippingPage"),
})

const shipmentDetailRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/shipments/$shipmentId",
    component: lazyRouteComponent(() => import("@/pages/production/ShipmentDetailPage"), "ShipmentDetailPage"),
})

const materialLotDetailRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/production/material-lots/$lotId",
    component: lazyRouteComponent(() => import("@/pages/production/MaterialLotDetailPage"), "MaterialLotDetailPage"),
})

const storageLocationsEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/editor/storage-locations",
    component: lazyRouteComponent(() => import("@/pages/editors/StorageLocationsEditorPage"), "StorageLocationsEditorPage"),
})

// Data Management tables without a home elsewhere: shifts (also the schedule's
// "Work hours" settings tab) and the life-limit definitions part types link to.
const shiftsEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/editor/shifts",
    component: lazyRouteComponent(() => import("@/pages/editors/ShiftsEditorPage"), "ShiftsEditorPage"),
})

const lifeLimitDefinitionsEditorRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/editor/life-limit-definitions",
    component: lazyRouteComponent(() => import("@/pages/editors/LifeLimitDefinitionsEditorPage"), "LifeLimitDefinitionsEditorPage"),
})

const lifeLimitDefinitionNewRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/editor/life-limit-definitions/new",
    component: lazyRouteComponent(() => import("@/pages/editors/forms/LifeLimitDefinitionFormPage"), "LifeLimitDefinitionFormPage"),
})

const lifeLimitDefinitionEditRoute = createRoute({
    getParentRoute: () => rootRoute, path: "/editor/life-limit-definitions/$id/edit",
    component: lazyRouteComponent(() => import("@/pages/editors/forms/LifeLimitDefinitionFormPage"), "LifeLimitDefinitionFormPage"),
})


// Legacy URLs (before 2026-10-05) → the /{domain}/{resource}/new and /$id/edit
// convention. Kept for a release so bookmarks and old links still land; remove after.
function legacyRedirect(path: string, to: (params: Record<string, string>) => string) {
    return createRoute({
        getParentRoute: () => rootRoute, path,
        // The query string rides along: `/dispositions/create?partId=…` must keep its part.
        beforeLoad: ({ params, location }) => {
            throw redirect({
                href: `${to(params as Record<string, string>)}${location.searchStr ?? ""}`,
                replace: true,
            });
        },
    });
}
const legacyRedirectRoutes = [
    legacyRedirect("/dispositions/create", () => "/production/dispositions/new"),
    legacyRedirect("/dispositions/edit/$id", (p) => `/production/dispositions/${p.id}/edit`),
    legacyRedirect("/PartTypeForm/create", () => "/editor/part-types/new"),
    legacyRedirect("/PartTypeForm/edit/$id", (p) => `/editor/part-types/${p.id}/edit`),
    legacyRedirect("/PartForm/create", () => "/editor/parts/new"),
    legacyRedirect("/PartForm/edit/$id", (p) => `/editor/parts/${p.id}/edit`),
    legacyRedirect("/ProcessForm/create", () => "/editor/processes/new"),
    legacyRedirect("/ProcessForm/edit/$id", (p) => `/editor/processes/${p.id}/edit`),
    legacyRedirect("/StepForm/create", () => "/editor/steps/new"),
    legacyRedirect("/StepForm/edit/$id", (p) => `/editor/steps/${p.id}/edit`),
    legacyRedirect("/EquipmentTypeForm/create", () => "/editor/equipment-types/new"),
    legacyRedirect("/EquipmentTypeForm/edit/$id", (p) => `/editor/equipment-types/${p.id}/edit`),
    legacyRedirect("/EquipmentForm/create", () => "/editor/equipment/new"),
    legacyRedirect("/EquipmentForm/edit/$id", (p) => `/editor/equipment/${p.id}/edit`),
    legacyRedirect("/ErrorTypeForm/create", () => "/editor/error-types/new"),
    legacyRedirect("/ErrorTypeForm/edit/$id", (p) => `/editor/error-types/${p.id}/edit`),
    legacyRedirect("/SamplingRuleSetForm/create", () => "/editor/sampling-rule-sets/new"),
    legacyRedirect("/SamplingRuleSetForm/edit/$id", (p) => `/editor/sampling-rule-sets/${p.id}/edit`),
    legacyRedirect("/DocumentTypeForm/create", () => "/editor/document-types/new"),
    legacyRedirect("/DocumentTypeForm/edit/$id", (p) => `/editor/document-types/${p.id}/edit`),
    legacyRedirect("/DocumentForm/create", () => "/documents/new"),
    legacyRedirect("/DocumentForm/edit/$id", (p) => `/documents/${p.id}/edit`),
    legacyRedirect("/WorkOrderForm/create", () => "/editor/work-orders/new"),
    legacyRedirect("/WorkOrderForm/edit/$id", (p) => `/editor/work-orders/${p.id}/edit`),
    legacyRedirect("/CompaniesForm/create", () => "/editor/companies/new"),
    legacyRedirect("/CompaniesForm/edit/$id", (p) => `/editor/companies/${p.id}/edit`),
    legacyRedirect("/UserForm/create", () => "/admin/users/new"),
    legacyRedirect("/UserForm/edit/$id", (p) => `/admin/users/${p.id}/edit`),
    legacyRedirect("/ThreeDModelsForm/create", () => "/editor/3d-models/new"),
    legacyRedirect("/ThreeDModelsForm/edit/$id", (p) => `/editor/3d-models/${p.id}/edit`),
    legacyRedirect("/ApprovalTemplateForm/create", () => "/editor/approval-templates/new"),
    legacyRedirect("/ApprovalTemplateForm/edit/$id", (p) => `/editor/approval-templates/${p.id}/edit`),
    legacyRedirect("/OrderForm", () => "/editor/orders/new"),
    legacyRedirect("/OrderForm/$id", (p) => `/editor/orders/${p.id}/edit`),
    legacyRedirect("/editor/qualityReports/create", () => "/editor/quality-reports/new"),
    legacyRedirect("/editor/qualityReports/edit/$id", (p) => `/editor/quality-reports/${p.id}/edit`),
    legacyRedirect("/editor/partTypes", () => "/editor/part-types"),
    legacyRedirect("/editor/equipmentTypes", () => "/editor/equipment-types"),
    legacyRedirect("/editor/errorTypes", () => "/editor/error-types"),
    legacyRedirect("/editor/samplingRuleSets", () => "/editor/sampling-rule-sets"),
    legacyRedirect("/editor/samplingrules", () => "/editor/sampling-rules"),
    legacyRedirect("/editor/WorkOrders", () => "/editor/work-orders"),
    legacyRedirect("/editor/ThreeDModels", () => "/editor/3d-models"),
    legacyRedirect("/editor/qualityReports", () => "/editor/quality-reports"),
    legacyRedirect("/editor/approvalTemplates", () => "/editor/approval-templates"),
    legacyRedirect("/editor/documentTypes", () => "/editor/document-types"),
    legacyRedirect("/TrainingRecordForm/$id", (p) => p.id === "new" ? "/quality/training/records/new" : `/quality/training/records/${p.id}/edit`),
    legacyRedirect("/TrainingTypeForm/$id", (p) => p.id === "new" ? "/quality/training/types/new" : `/quality/training/types/${p.id}/edit`),
    legacyRedirect("/CalibrationRecordForm/$id", (p) => p.id === "new" ? "/quality/calibrations/records/new" : `/quality/calibrations/records/${p.id}/edit`),
];

const routeTree = rootRoute.addChildren([...legacyRedirectRoutes, shiftsEditorRoute, lifeLimitDefinitionsEditorRoute, lifeLimitDefinitionNewRoute, lifeLimitDefinitionEditRoute, schedulingGanttRoute, schedulingCalendarRoute, laborHoursRoute, capacityPlanningRoute, forecastRoute, orderBookRoute, staffOrderRoute, stagingRoute, requirementsRoute, stepTimingsRoute, stepEquipmentAffinitiesRoute, workCenterChangeoversRoute, MaterialsEditorRoute, materialCreateRoute, materialEditRoute, storageLocationsEditorRoute, materialLotDetailRoute, receivingMetricsRoute, shipmentsRoute, shipmentDetailRoute, locationsRoute, locationDetailRoute, cycleCountsRoute, cycleCountRoute, homeRoute, loginRote, signupRoute, passwordResetRequestRoute, passwordResetConfirmRoute, trackerRoute, orderDetailsRoute, partAnnotatorRoute, heatMapViewerPartTypeRoute, heatMapViewerPartRoute, heatmapRoute, QAPage, ordersCreateFormRoute, ordersEditFormRoute, editLandingPageRoute, OrdersEditorPageRoute, PartsEditorRoute, partCreateRoute, partEditRoute, PartTypesEditorRoute, partTypeCreateRoute, partTypeEditRoute, processCreateRoute, processEditRoute, ProcessEditorRoute, stepCreateRoute, stepEditRoute, StepEditorRoute, equipmentCreateRoute, equipmentEditRoute, EquipmentEditorRoute, ToolingEditorRoute, toolingCreateRoute, toolingEditRoute, equipmentTypeCreateRoute, equipmentTypeEditRoute, EquipmentTypeEditorRoute, errorTypeCreateRoute, errorTypeEditRoute, ErrorTypeEditorRoute, DocumentsRoute, DocumentsListRoute, DocumentDetailRoute, SamplingRulesEditorRoute, SamplingRuleSetsEditorRoute, samplingRuleSetsCreateRoute, samplingRuleSetsEditRoute, DocumentCreateRoute, DocumentEditRoute, ModelDetailRoute, WorkOrderEditorRoute, workOrderEditRoute, workOrderCreateRoute, workOrderDetailRoute, workOrdersControlCenterRoute, workOrderControlRoute, companiesEditorRoute, companiesEditRoute, companiesCreateRoute, userEditorRoute, usersEditRoute, usersCreateRoute, userManagementRoute, bulkUserActionsRoute, userDetailRoute, workCentersAdminRoute, aiChatRoute, threeDModelsEditorRoute, threeDModelsCreateRoute, threeDModelsEditRoute, userProfileRoute, settingsRoute, organizationSettingsRoute, brandingSettingsRoute, notificationRulesSettingsRoute, notificationRuleNewRoute, notificationRuleEditRoute, notificationScheduleNewRoute, notificationScheduleEditRoute, notificationDefaultsSettingsRoute, externalContactsRoute, externalContactNewRoute, externalContactEditRoute, myNotificationsRoute, notificationFeedRoute, billingSettingsRoute, milestonesEditorRoute, integrationsSettingsRoute, integrationDetailRoute, qualityReportsEditorRoute, qualityReportCreateRoute, qualityReportEditRoute, annotatorPageRoute, analysisRoute, processFlowRoute, spcRoute,qualityDashboardRoute, changeControlRoute, pcrDetailRoute, pcoDetailRoute, pcnDetailRoute, capaListRoute, capaCreateRoute, capaDetailRoute, ncrAnalysisRoute, defectAnalysisRoute, trainingDashboardRoute, trainingOverridesRoute, trainingRecordsRoute, trainingTypesRoute, trainingMatrixRoute, jobRolesRoute, jobRoleNewRoute, jobRoleEditRoute, trainingRecordFormRoute, trainingRecordCreateRoute, trainingTypeFormRoute, trainingTypeCreateRoute, calibrationDashboardRoute, measurementDefinitionsRoute, calibrationRecordsRoute, calibrationRecordFormRoute, calibrationRecordCreateRoute, calibrationExposureRoute, inboxRoute, workOrdersRoute, dispositionsRoute, dispositionCreateRoute, dispositionEditRoute, auditLogRoute, masterWorkbookRoute, approvalTemplatesEditorRoute, approvalTemplateCreateRoute, approvalTemplateEditRoute, approvalsOverviewRoute, approvalsHistoryRoute, documentTypesEditorRoute, documentTypeCreateRoute, documentTypeEditRoute, groupsEditorRoute, groupDetailRoute, forbiddenRoute, schemaAuditRoute, operatorHomePrototypeRoute, workQueuePrototypeRoute, qaHomePrototypeRoute, homeLandingsSpikeRoute, trainingMatrixPrototypeRoute, qaInboxRoute, operatorHomeRoute, shiftNotesRoute, remanDashboardRoute, coresEditorRoute, coreDetailRoute, coreReceiveRoute, coreReceiveBatchRoute, coreLotsRoute, coreDisassemblyRoute, rebuildQueueRoute, coreRebuildRoute, repairCodesEditorRoute, repairCodeNewRoute, repairCodeEditRoute, rebuildLevelsEditorRoute, rebuildLevelNewRoute, rebuildLevelEditRoute, harvestedComponentsRoute, materialLotsRoute, receiveLotsBatchRoute, receivingInspectionQueueRoute, receivingInspectionRoute, incomingHubRoute, outsideProcessingBoardRoute, receivingPlansRoute, receivingPlanEditorRoute, receivingPlanSubstepsRoute, supplierQualityRoute, supplierQualificationsRoute, supplierQualificationNewRoute, supplierQualificationEditRoute, partApprovalsRoute, partApprovalNewRoute, partApprovalEditRoute, dwiSpikeRoute, substepEditorRoute, operatorSubstepRuntimeRoute])

// Create router with context
export function createAppRouter(queryClient: QueryClient) {
    return createRouter({
        routeTree,
        defaultPreload: 'intent', // Preload on hover/focus
        defaultPreloadStaleTime: 5000, // Keep prefetched data fresh for 5 seconds
        defaultPendingMs: 200, // Only show loading if navigation takes >200ms
        defaultPendingComponent: DefaultPendingComponent,
        defaultErrorComponent: DefaultErrorComponent,
        defaultNotFoundComponent: DefaultNotFoundComponent,
        context: {
            queryClient,
        },
    });
}

// Type for the router
export type AppRouter = ReturnType<typeof createAppRouter>

declare module "@tanstack/react-router" {
    interface Register {
        router: AppRouter;
    }
}